import { useMemo } from 'react';

import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import { useAllowedFacilities } from 'hooks/useAllowedFacilities';
import Workflow, { WorkflowAction } from 'models/Workflow';

import { collectedFacilityLabel, FacilityOption, VALUE_NAMES } from '../constants';
import FacilityMappingReview, { FacilityPicks } from '../FacilityMappingReview';

import { MAPPED_FACILITIES_VALUE } from './fieldResolver';
import { FieldRow, ReadOnlyValue } from './TaskSection';

/**
 * The Facility section, which is not one control (OLIO-3022).
 *
 * Which rendering appears is decided by what the run is actually waiting on, never by the
 * retrieval mode: a script-captured facility whose string the client has already mapped resolves
 * silently and never asks anybody anything, so "captured by a script" does not imply "unmapped".
 *
 *   1. the mapping step is waiting     -> the mapping review, lifted whole from the step view
 *   2. retrieval is waiting            -> a plain pick from this user's own facilities
 *   3. neither                         -> what the facilities resolved to, read-only
 *
 * Several facilities are supported in all three, QUIETLY: the controls handle more than one, and
 * nothing says so. No `+ Add facility`, no plural labels, no empty second row — a portal that
 * never names two never teaches anyone it could.
 */

interface Props {
  workflow: Workflow;
  label: string;
  present: boolean;
  mapping?: WorkflowAction;
  retrieval?: WorkflowAction;
  picks: FacilityPicks;
  onPicksChange: (picks: FacilityPicks) => void;
  // The picks made when RETRIEVAL owns the section — this user's own facilities, already
  // resolved, so nothing needs mapping afterwards.
  entered: (FacilityOption | null)[];
  onEnteredChange: (picks: (FacilityOption | null)[]) => void;
}

export default function FacilitySection({
  workflow,
  label,
  present,
  mapping,
  retrieval,
  picks,
  onPicksChange,
  entered,
  onEnteredChange,
}: Props) {
  if (mapping) {
    return (
      <div data-testid='facility-section-mapping'>
        <FacilityMappingReview action={mapping} picks={picks} onPicksChange={onPicksChange} />
      </div>
    );
  }

  if (retrieval) {
    return <FacilityPicker label={label} present={present} entered={entered} onEnteredChange={onEnteredChange} />;
  }

  return <MappedFacilities workflow={workflow} label={label} present={present} />;
}

// The entry path: the person names the facility themselves, so what this collects is an Olio
// facility and the mapping step has nothing to resolve. A second row appears only once the first
// is filled — support for several, without an affordance announcing it.
function FacilityPicker({
  label,
  present,
  entered,
  onEnteredChange,
}: Pick<Props, 'label' | 'present' | 'entered' | 'onEnteredChange'>) {
  const { facilities } = useAllowedFacilities();
  const options: FacilityOption[] = useMemo(
    () => facilities.map((group) => ({ id: group.id, name: group.name })),
    [facilities]
  );

  const rows = entered.length > 0 && entered[entered.length - 1] ? [...entered, null] : entered;

  const setRow = (index: number, option: FacilityOption | null) => {
    const next = rows.map((pick, i) => (i === index ? option : pick));
    // Trailing empties are never kept: the extra row exists to be filled, not to persist as an
    // empty control once the person changes their mind.
    while (next.length > 1 && !next[next.length - 1]) next.pop();
    onEnteredChange(next);
  };

  return (
    <div data-testid='facility-section-picker'>
      {rows.map((row, index) => (
        // Index keys: rows are positional (no identity beyond their slot).
        <FieldRow key={index} label={index === 0 ? label : ''} present={index === 0 ? present : !!row}>
          <Select<FacilityOption>
            inputId={`task-facility-${index}`}
            placeholder='Select a facility'
            options={options.filter(
              (option) => option.id === row?.id || !entered.some((pick) => pick?.id === option.id)
            )}
            value={row}
            onChange={(option) => setRow(index, option)}
            menuPortalTarget={document.body}
            getOptionValue={(option) => option.id}
            getOptionLabel={(option) => option.name}
          />
        </FieldRow>
      ))}
    </div>
  );
}

// Already settled. The mapped ids are bare uuids in `values[]` — the option list that named them
// lived on the mapping action's staged result and is gone once it completed — so the names are
// resolved against this user's allowed facilities, with the portal's own collected string as the
// fallback. A mapped facility can legitimately sit outside the acting user's allowed set, which
// is why an id we cannot name says so rather than reading as a facility called "facility".
function MappedFacilities({ workflow, label, present }: Pick<Props, 'workflow' | 'label' | 'present'>) {
  const { facilities } = useAllowedFacilities();
  const mappedIds = (workflow.valueFor(MAPPED_FACILITIES_VALUE)?.value ?? []) as unknown;
  const collected = (workflow.valueFor(VALUE_NAMES.FACILITIES)?.value ?? []) as unknown;
  const collectedEntries = Array.isArray(collected) ? collected : [];

  const ids = Array.isArray(mappedIds) ? mappedIds : [];
  // The collected labels are only trustworthy positionally when the two lists actually
  // correspond — they come from different steps (collection vs resolution), so on any length
  // mismatch a borrowed label could name an id with a DIFFERENT facility's portal string,
  // which is worse than the bare fallback below.
  const aligned = ids.length === collectedEntries.length;

  const names = ids.map((id, index) => {
    const named = facilities.find((facility) => facility.id === id)?.name;
    if (named) return named;
    const fromCollection =
      aligned && collectedEntries[index] ? collectedFacilityLabel(collectedEntries[index], []) : '';
    return fromCollection && fromCollection !== 'facility' ? fromCollection : `Facility ${String(id)}`;
  });

  return (
    <div data-testid='facility-section-mapped'>
      <FieldRow label={label} present={present}>
        <ReadOnlyValue value={names.join(', ')} />
      </FieldRow>
    </div>
  );
}
